import supabase from '../supabase/supabase';

const TABLES = {
  profiles: 'hms_industry_profiles_1715730000000',
  versions: 'hms_industry_profile_versions_1715730000000',
  hazards: 'hms_psychosocial_hazards_1715705000000',
  factors: 'hms_protective_factors_1715707000000',
  questions: 'assessment_questions_1715680000000',
  hazardMappings: 'hms_industry_profile_hazard_mappings_1715731000000',
  factorMappings: 'hms_industry_profile_protective_factor_mappings_1715731000000',
  questionMappings: 'hms_industry_profile_question_mappings_1715731000000'
};

const unwrap = ({ data, error }) => {
  if (error) throw error;
  return data;
};

export async function verifyIndustryProfileAdminAccess() {
  const { data: userResult, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!userResult.user) return false;

  const { data, error } = await supabase.rpc(
    'hms_user_is_superadmin_1715705000000'
  );
  if (error) throw error;
  return data === true;
}

export async function loadIndustryProfileAdminData() {
  const [profiles, versions, hazards, factors, questions] = await Promise.all([
    supabase.from(TABLES.profiles)
      .select('id,profile_key,created_at')
      .order('profile_key'),
    supabase.from(TABLES.versions)
      .select('id,profile_id,version_number,display_name,description,status,created_at,published_at,retired_at')
      .order('created_at', { ascending: false }),
    supabase.from(TABLES.hazards)
      .select('id,code,title,description,status')
      .is('organization_id', null)
      .order('code'),
    supabase.from(TABLES.factors)
      .select('id,code,name,description,status')
      .is('organization_id', null)
      .order('code'),
    supabase.from(TABLES.questions)
      .select('id,question_key,question_text,dimension,status')
      .order('question_key')
  ]);

  return {
    profiles: unwrap(profiles) || [],
    versions: unwrap(versions) || [],
    catalogs: {
      hazards: unwrap(hazards) || [],
      factors: unwrap(factors) || [],
      questions: unwrap(questions) || []
    }
  };
}

export async function loadIndustryProfileMappings(profileVersionId) {
  const [hazards, factors, questions] = await Promise.all([
    supabase.from(TABLES.hazardMappings).select('*')
      .eq('industry_profile_version_id', profileVersionId),
    supabase.from(TABLES.factorMappings).select('*')
      .eq('industry_profile_version_id', profileVersionId),
    supabase.from(TABLES.questionMappings).select('*')
      .eq('industry_profile_version_id', profileVersionId)
  ]);

  return {
    hazards: unwrap(hazards) || [],
    factors: unwrap(factors) || [],
    questions: unwrap(questions) || []
  };
}

export async function createIndustryProfileDraft({ profileKey, displayName, description }) {
  const { data, error } = await supabase.rpc(
    'hms_industry_profile_admin_create_draft_v1',
    {
      p_profile_key: profileKey,
      p_display_name: displayName,
      p_description: description || null
    }
  );
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

const mappingRpc = {
  hazard: 'hms_industry_profile_admin_add_hazard_mapping_v1',
  protective_factor: 'hms_industry_profile_admin_add_factor_mapping_v1',
  question: 'hms_industry_profile_admin_add_question_mapping_v1'
};

const targetArgument = {
  hazard: 'p_hazard_id',
  protective_factor: 'p_protective_factor_id',
  question: 'p_question_id'
};

export async function addIndustryProfileMapping(type, profileVersionId, targetId, values) {
  const args = {
    p_profile_version_id: profileVersionId,
    [targetArgument[type]]: targetId,
    p_relevance_class: values.relevanceClass,
    p_rationale: values.rationale,
    p_applicability_condition: values.applicabilityCondition || null,
    p_source_reference: values.sourceReference || null
  };
  const { data, error } = await supabase.rpc(mappingRpc[type], args);
  if (error) throw error;
  return data;
}

const bulkMappingGroups = [
  {
    key: 'hazards',
    type: 'hazard',
    identityField: 'code'
  },
  {
    key: 'protectiveFactors',
    type: 'protective_factor',
    identityField: 'code'
  },
  {
    key: 'questions',
    type: 'question',
    identityField: 'question_key'
  }
];

const duplicateMappingErrors = {
  hazard: {
    rpcMessage: 'this hazard is already mapped to the profile version.',
    constraint: 'ip_hazard_mapping_version_hazard_uq_1715731000000'
  },
  protective_factor: {
    rpcMessage: 'this protective factor is already mapped to the profile version.',
    constraint: 'ip_factor_mapping_version_factor_uq_1715731000000'
  },
  question: {
    rpcMessage: 'this question is already mapped to the profile version.',
    constraint: 'ip_question_mapping_version_question_uq_1715731000000'
  }
};

const isDuplicateMappingError = (error, type) => {
  const signature = duplicateMappingErrors[type];
  const errorText = [
    error?.message,
    error?.details,
    error?.hint
  ].filter(Boolean).join(' ').toLowerCase();

  if (errorText.includes(signature.rpcMessage)) return true;

  return error?.code === '23505' &&
    errorText.includes(signature.constraint);
};

export async function applyIndustryProfileMappingPackage(profileVersionId, entriesByGroup) {
  const results = [];

  for (const group of bulkMappingGroups) {
    const entries = Array.isArray(entriesByGroup?.[group.key])
      ? entriesByGroup[group.key]
      : [];

    for (const entry of entries) {
      const identity = entry?.[group.identityField] ?? '';
      const targetId = entry?.targetId ?? null;

      if (entry?.status === 'Allerede koblet') {
        results.push({
          type: group.type,
          [group.identityField]: identity,
          targetId,
          status: 'Allerede koblet'
        });
        continue;
      }

      if (entry?.status !== 'Klar' || !targetId) {
        results.push({
          type: group.type,
          [group.identityField]: identity,
          targetId,
          status: 'Hoppet over'
        });
        continue;
      }

      try {
        await addIndustryProfileMapping(
          group.type,
          profileVersionId,
          targetId,
          {
            relevanceClass: entry.relevance,
            rationale: entry.rationale,
            applicabilityCondition: entry.applicability_condition,
            sourceReference: entry.source_reference
          }
        );
        results.push({
          type: group.type,
          [group.identityField]: identity,
          targetId,
          status: 'Opprettet'
        });
      } catch (error) {
        results.push({
          type: group.type,
          [group.identityField]: identity,
          targetId,
          status: isDuplicateMappingError(error, group.type) ? 'Allerede koblet' : 'Feilet',
          error: error?.message || String(error)
        });
      }
    }
  }

  return results;
}

export async function updateIndustryProfileMapping(type, mappingId, values) {
  const { data, error } = await supabase.rpc(
    'hms_industry_profile_admin_update_mapping_v1',
    {
      p_mapping_type: type,
      p_mapping_id: mappingId,
      p_relevance_class: values.relevanceClass,
      p_rationale: values.rationale,
      p_applicability_condition: values.applicabilityCondition || null,
      p_source_reference: values.sourceReference || null
    }
  );
  if (error) throw error;
  return data;
}

export async function deleteIndustryProfileMapping(type, mappingId) {
  const { data, error } = await supabase.rpc(
    'hms_industry_profile_admin_delete_mapping_v1',
    { p_mapping_type: type, p_mapping_id: mappingId }
  );
  if (error) throw error;
  return data;
}

export async function publishIndustryProfileVersion(profileVersionId) {
  const { data, error } = await supabase.rpc(
    'hms_industry_profile_admin_publish_v1',
    { p_profile_version_id: profileVersionId }
  );
  if (error) throw error;
  return data;
}

export async function retireIndustryProfileVersion(profileVersionId) {
  const { data, error } = await supabase.rpc(
    'hms_industry_profile_admin_retire_v1',
    { p_profile_version_id: profileVersionId }
  );
  if (error) throw error;
  return data;
}